import pick from 'lodash/pick';

import { Model } from '@/models/Model';
import {
  AddOptions,
  CacheStrategy,
  UpdateItem,
  UpdateOptions,
} from './strategy/CacheStrategy';

export interface CacheCommandI<T> {
  execute(): void;
  undo(): void;
  strategy: CacheStrategy<T>;
}

export abstract class CacheCommand<T> implements CacheCommandI<T> {
  public strategy: CacheStrategy<T>;

  constructor(strategy: CacheStrategy<T>) {
    this.strategy = strategy;
  }

  abstract execute(): void;

  abstract undo(): void;
}

abstract class SnapshotCacheCommand<T> extends CacheCommand<T> {
  private previousState: T | T[] | undefined;

  execute(): void {
    this.previousState = this.strategy.get();
  }

  undo(): void {
    if (this.previousState) {
      this.strategy.set(this.previousState);
    }
  }
}

export class AddCommand<T extends Model> extends CacheCommand<T> {
  constructor(
    strategy: CacheStrategy<T>,
    private data: T,
    private options?: AddOptions,
  ) {
    super(strategy);
  }

  execute(): void {
    this.strategy.add?.(this.data, this.options);
  }

  undo(): void {
    this.strategy.remove(this.data.id);
  }
}

export class UpdateCommand<T extends Model> extends CacheCommand<T> {
  private previousData?: UpdateItem<T>;

  constructor(
    strategy: CacheStrategy<T>,
    private data: UpdateItem<T>,
    private options?: UpdateOptions,
  ) {
    super(strategy);
  }

  execute(): void {
    const current = this.strategy.getById?.(this.data.id);
    const patchedFields = Object.keys(this.data) as (keyof T)[];

    this.previousData = current && {
      ...pick(current, patchedFields),
      id: this.options?.newId || this.data.id,
    };

    this.strategy.update?.(this.data, this.options);
  }

  undo(): void {
    if (!this.previousData) return;

    this.strategy.update?.(this.previousData, { newId: this.data.id });
  }
}

export class RemoveCommand<T extends Model> extends SnapshotCacheCommand<T> {
  constructor(strategy: CacheStrategy<T>, private id: number) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.remove(this.id);
  }
}

export class SetCommand<T extends Model> extends SnapshotCacheCommand<T> {
  constructor(strategy: CacheStrategy<T>, private data: T | T[]) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.set(this.data);
  }
}

export class MergeCommand<T extends Model> extends SnapshotCacheCommand<T> {
  constructor(strategy: CacheStrategy<T>, private data: Partial<T>) {
    super(strategy);
  }

  execute(): void {
    super.execute();
    this.strategy.merge(this.data);
  }
}
